import { useQuery, type UseQueryResult } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import type { ReviewId } from "../../data/review-data"
import { fromWireReport, type Report, type ReportView, type WireReport } from "../report-data"

export interface ReportQueryOptions {
  /**
   * Also list the findings already corrected (`include_resolved`). The
   * verdict, counts and summary stay those of the outstanding work; only the
   * findings and sections grow.
   */
  includeCorrected?: boolean
  /** False holds the request, for a listing nobody has asked to see yet. */
  enabled?: boolean
}

/**
 * `GET /reviews/{id}/report`, shaped for the output screen.
 *
 * `view` is a request, not a guarantee: the server answers a client principal
 * with the client view whatever is asked for, because the view is a disclosure
 * boundary. A client whose findings are not released yet gets a 409.
 */
export function useReportQuery(
  reviewId: ReviewId | null,
  view: ReportView = "client",
  { includeCorrected = false, enabled = true }: ReportQueryOptions = {},
): UseQueryResult<Report> {
  const listing = includeCorrected ? "with-corrected" : "outstanding"
  return useQuery({
    queryKey:
      reviewId != null
        ? qk.reviews.report(reviewId, view, listing)
        : ["reviews", "report", "none", listing],
    queryFn: async ({ signal }) => {
      const wire = await http.get<WireReport>(
        `/api/v1/reviews/${encodeURIComponent(reviewId ?? "")}/report`,
        {
          // Sent only when asked for, so the ordinary request stays as it was.
          params: includeCorrected ? { view, include_resolved: true } : { view },
          signal,
        },
      )
      return fromWireReport(wire)
    },
    enabled: reviewId != null && enabled,
  })
}
